import React, { useState } from 'react';
import {
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Contact = ({ hasConsent, onRequireConsent }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    servicio: '',
    fecha: '',
    mensaje: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

// Reemplaza el handleSubmit por este:
const handleSubmit = (e) => {
  e.preventDefault();

  // Verificar que los campos requeridos estén llenos
  if (!formData.nombre || !formData.telefono || !formData.servicio) {
    setFormStatus('campos-requeridos');
    setTimeout(() => setFormStatus(''), 3000);
    return;
  }

  // Verificar consentimiento de privacidad antes de procesar el formulario
  if (!hasConsent) {
    // Si no hay consentimiento, mostrar el modal y bloquear el envío
    if (onRequireConsent) {
      onRequireConsent();
    }
    setFormStatus('sin-consentimiento');
    setTimeout(() => setFormStatus(''), 3000);
    return;
  }

  // ⚙️ Construye el texto que se enviará por WhatsApp
  const msg = `
    Hola, soy *${formData.nombre}*.
    Quiero agendar una cita para *${formData.servicio || '—'}*.
    Fecha preferida: ${formData.fecha || 'por definir'}.
    Teléfono: ${formData.telefono}.
    Email: ${formData.email || '—'}.
    Mensaje adicional:
    ${formData.mensaje || '—'}
  `.trim();

  // 🚀 Codifica y genera URL de WhatsApp (formato internacional sin símbolos)
  const phone = '528112419864'; // +52 81 1745 317
  const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;

  // 🔗 Abre WhatsApp en una nueva pestaña
  window.open(waUrl, '_blank');

  // 🧹 Resetea estado y muestra confirmación visual
  setFormStatus('enviado');
  setTimeout(() => {
    setFormStatus('');
    setFormData({
      nombre: '',
      telefono: '',
      email: '',
      servicio: '',
      fecha: '',
      mensaje: ''
    });
  }, 3000);
};


const servicios = [
  'Consulta cardiológica',
  'Consulta urgente',
  'Consulta paciente diabético',
  'Consulta paciente con accidente vascular cerebral (embolia)',
  'Paciente con marcapaso cardiaco',
  'Control de hipertensión arterial',
  'Cateterismo cardíaco',
  'Angioplastia con stents coronarios',
  'Ecocardiograma en reposo',
  'Ecocardiograma con esfuerzo',
  'Ecografía Doppler',
  'Ecografía Doppler carotídea',
  'Ecografía Doppler arterial periférico',
  'Ecografía Doppler venoso periférico',
  'Arritmias cardíacas',
  'Colocación de marcapaso definitivo',
  'Electrocardiograma (ECG)',
  'Holter cardiaco 24 hrs',
  'Ablación cardíaca',
  // Prevención
  'Check up cardiovascular',
  'Control de paciente diabético',
  'Control de paciente con hipertensión arterial',
  'Control de paciente adulto mayor',
  'Control de dislipidemias (colesterol y triglicéridos)',
  'Control de paciente post-infarto',
  'Control de paciente post-embolia'
  //'Consulta nutricional',
  //'Programa anti-tabaco'
];

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: "Teléfonos",
      phones: [
        { number: "(81) 8343-2180", link: "tel:+528183432180" },
        { number: "(81) 8343-2188", link: "tel:+528183432188" },
        { number: "(81) 8343-2189", link: "tel:+528183432189" }
      ],
      description: "Llámanos para agendar tu cita"
    },
    {
      icon: MapPinIcon,
      title: "Ubicación",
      content: "Calle Privada José Marroquín Leal 221 Norte",
      link: "https://goo.gl/maps/2v2hQv2nQv2hQv2n7",
      description: "Centro, Monterrey, N.L. 64000"
    },
    {
      icon: ClockIcon,
      title: "Horarios",
      content: "Lunes a Viernes: 8:00 - 20:00 hrs",
      secondaryContent: "Sábado: 8:00 - 14:00 hrs",
      link: "#",
      description: "Domingo: Cerrado"
    },
    {
      icon: EnvelopeIcon,
      title: "Email",
      content: "cardiologianorte221@outlook.com",
      link: "mailto:cardiologianorte221@outlook.com",
      description: "Escríbenos tus consultas"
    }
  ];

  // Función para verificar si todos los campos requeridos están llenos
const camposRequeridosLlenos = () => {
  return formData.nombre && formData.telefono && formData.servicio;
};

// Función para manejar el click del botón cuando no hay consentimiento
const handleButtonClick = (e) => {
  if (!hasConsent && camposRequeridosLlenos()) {
    e.preventDefault();
    if (onRequireConsent) {
      onRequireConsent();
    }
  }
};

  return (
    <section id="contacto" className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-accent/20 text-accent text-sm font-semibold rounded-full mb-4 font-sans">
            <CalendarDaysIcon className="h-4 w-4 mr-2" />
            Contacto
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-primary mb-4 sm:mb-6">
            Agenda tu <span className="text-marino">Consulta</span>
          </h2>
          <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed px-4 font-sans text-justify">
            Estamos aquí para cuidar de tu salud cardiovascular.
            Contáctanos para agendar tu cita o resolver cualquier duda.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 sm:gap-8 lg:gap-12 xl:gap-16">
                <div className="order-2 xl:order-1 xl:col-span-2">
                <div className="bg-surface rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-border shadow-card">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-serif font-bold text-primary mb-4 sm:mb-6">
                  Solicita tu Cita
                  </h3>

                  {formStatus === 'enviado' && (
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-success-50 border border-success-200 rounded-lg flex items-start sm:items-center">
                    <CheckCircleIcon className="h-5 w-5 text-success mr-3 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <span className="text-success-700 text-sm sm:text-base font-sans">¡Mensaje enviado exitosamente! Te contactaremos pronto.</span>
                  </div>
                  )}

                  {formStatus === 'campos-requeridos' && (
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start sm:items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <span className="text-red-800 text-sm sm:text-base font-sans">
                      Por favor, completa todos los campos requeridos (*).
                    </span>
                  </div>
                  )}

                  {formStatus === 'sin-consentimiento' && (
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start sm:items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <span className="text-amber-800 text-sm sm:text-base font-sans">
                      Debes aceptar el Aviso de Privacidad para poder enviar el formulario.
                    </span>
                  </div>
                  )}

                  {!hasConsent && (
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800 text-sm sm:text-base font-sans">
                      <strong>Información:</strong> Para utilizar este formulario, debes aceptar nuestro Aviso de Privacidad.
                    </p>
                  </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-primary mb-2 font-sans">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm sm:text-base font-sans bg-background text-text-primary"
                      placeholder="Tu nombre completo"
                    />
                    </div>
                    <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-primary mb-2 font-sans">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm sm:text-base font-sans bg-background text-text-primary"
                      placeholder="(81) 1234-5678"
                    />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-primary mb-2 font-sans">
                    Correo Electrónico
                    </label>
                    <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm sm:text-base font-sans bg-background text-text-primary"
                    placeholder="tu@email.com"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                    <label htmlFor="servicio" className="block text-sm font-medium text-primary mb-2 font-sans">
                      Servicio de Interés *
                    </label>
                    <select
                      id="servicio"
                      name="servicio"
                      value={formData.servicio}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm sm:text-base font-sans bg-background text-text-primary"
                    >
                      <option value="">Selecciona un servicio</option>
                      {servicios.map((servicio, index) => (
                      <option key={index} value={servicio}>
                        {servicio}
                      </option>
                      ))}
                    </select>
                    </div>
                    <div>
                    <label htmlFor="fecha" className="block text-sm font-medium text-primary mb-2 font-sans">
                      Fecha Preferida
                    </label>
                    <input
                      type="date"
                      id="fecha"
                      name="fecha"
                      value={formData.fecha}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm sm:text-base font-sans bg-background text-text-primary"
                    />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="mensaje" className="block text-sm font-medium text-primary mb-2 font-sans">
                    Mensaje Adicional
                    </label>
                    <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm sm:text-base resize-none font-sans bg-background text-text-primary"
                    placeholder="Cuéntanos sobre tus síntomas o dudas..."
                    />
                  </div>

<button
  type="submit"
  onClick={handleButtonClick}
  disabled={!camposRequeridosLlenos()}
  className={`w-full font-semibold py-3 sm:py-4 px-6 rounded-lg transition-all duration-300 text-sm sm:text-base font-sans ${
    camposRequeridosLlenos() && hasConsent
      ? 'bg-primary text-white hover:bg-primary/90 shadow-button hover:shadow-button-hover cursor-pointer'
      : camposRequeridosLlenos() && !hasConsent
      ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-button hover:shadow-button-hover cursor-pointer'
      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
  }`}
>
  {!camposRequeridosLlenos()
    ? 'Completa los Campos Requeridos (*)'
    : hasConsent
    ? 'Enviar Solicitud'
    : 'Acepta el Aviso de Privacidad para Continuar'
  }
</button>

                  </form>

          {/* Componente de alerta para emergencias médicas, con semántica y accesibilidad mejoradas */}


            </div>
          </div>

          {/* Contact Info & Map */}
          <div className="order-1 xl:order-2 xl:col-span-3 space-y-6 sm:space-y-8">
            {/* Contact Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="group bg-surface hover:bg-accent/5 p-4 sm:p-6 rounded-xl transition-all duration-300 hover:shadow-card border border-border hover:border-accent/20"
                >
                  <div className="flex items-start">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary group-hover:bg-secondary rounded-lg flex items-center justify-center mr-3 sm:mr-4 transition-colors duration-300 flex-shrink-0">
                      <info.icon className="h-5 w-5 sm:h-6 sm:w-6 text-accent group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-primary mb-2 text-sm sm:text-base font-serif">
                        {info.title}
                      </h4>

                      {/* Renderizado especial para teléfonos */}
                      {info.phones ? (
                        <div className="space-y-1 mb-2">
                          {info.phones.map((phone, phoneIndex) => (
                            <a
                              key={phoneIndex}
                              href={phone.link}
                              className="block text-black hover:text-primary font-medium text-sm sm:text-base transition-colors duration-200 font-sans"
                            >
                              {phone.number}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <>
                          {/* Renderizado para otros elementos de contacto */}
                          {info.link ? (
                            <a
                              href={info.link}
                              className="text-black hover:text-primary font-medium mb-1 text-sm sm:text-base break-words block transition-colors duration-200 font-sans"
                            >
                              {info.content}
                            </a>
                          ) : (
                            <p className="text-accent font-medium mb-1 text-sm sm:text-base break-words font-sans">
                              {info.content}
                            </p>
                          )}
                          {info.secondaryContent && (
                            <p className="text-secondary font-medium mb-1 text-sm break-words font-sans">
                              {info.secondaryContent}
                            </p>
                          )}
                        </>
                      )}

                      <p className="text-xs sm:text-sm text-text-secondary font-sans">
                        {info.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="bg-surface rounded-2xl p-4 sm:p-6 border border-border shadow-card">
              <h3 className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-8">
                Nuestra Ubicación
              </h3>
                              <div className="aspect-video rounded-lg overflow-hidden shadow-card bg-background relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3595.791116525134!2d-100.32269872460235!3d25.67821817740443!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x866295890decbdff%3A0xa1a235b5b05069e7!2sCentro%20Medico%20Cardiolog%C3%ADa%20del%20Norte!5e0!3m2!1ses-419!2smx!4v1752891745823!5m2!1ses-419!2smx"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  title="Ubicación Centro Medico Cardiología del Norte"
                  className="w-full h-full"
                />
                {/* Fallback para cuando el mapa no carga */}
                <div className="absolute inset-0 bg-background flex items-center justify-center opacity-0 pointer-events-none">
                  <div className="text-center p-4">
                    <p className="text-text-secondary mb-2">Ver ubicación en:</p>
                    <a
                      href="https://maps.app.goo.gl/MpQgof5vZ1VrPqEk8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors duration-300 font-medium"
                    >
                      Google Maps
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-surface rounded-lg border border-border">
                <p className="text-xs sm:text-sm text-text-primary leading-relaxed font-sans mb-3">
                  <strong>Dirección:</strong> Calle Privada José Marroquín Leal 221 Norte, Centro, Monterrey, N.L. 64000<br/>
                  <strong>Referencias:</strong> Centro de Monterrey
                </p>
                <a
                  href="https://maps.app.goo.gl/MpQgof5vZ1VrPqEk8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-xs px-3 py-2 bg-primary/10 text-primary hover:bg-primary hover:text-white rounded-lg transition-all duration-300 font-medium"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  Abrir en Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Contact;
